import { useEffect, useMemo, useState } from 'react';
import { createModel, useModel } from 'src/common/create-model';
import { useBitablePageSize } from 'src/hooks/collection-view/use-collection-view';
import { getState } from 'src/redux/store';
import { $appUiStateCache } from 'src/services/app';
import { $searchParams } from 'src/utils';
import { useBitable } from './context';

export const getPropertySegments = (
  recordId: string,
  propertyId: string,
  blocks = getState().blocks
) => {
  return blocks[recordId]?.data.collectionProperties?.[propertyId];
};

const useBitableLoadMore = (props: { disable?: boolean; recordIds?: string[] }) => {
  const { viewId } = useBitable();
  const { disable, recordIds: originRecordIds = [] } = props;
  const pageSize = useBitablePageSize(viewId);
  const [loadPages, setLoadPages] = useState(pageSize);

  useEffect(() => {
    setLoadPages(pageSize);
  }, [pageSize]);

  const recordIds = useMemo(() => {
    if (disable || $searchParams.print) return originRecordIds;

    const ids = originRecordIds.slice(0, loadPages);
    const newCreateRecord = $appUiStateCache.$newCreatedRecord;
    if (newCreateRecord) {
      if (
        viewId === newCreateRecord.viewId &&
        originRecordIds.includes(newCreateRecord.id) &&
        !ids.includes(newCreateRecord.id)
      ) {
        ids.push(newCreateRecord.id);
      }
    }
    return ids;
  }, [disable, loadPages, originRecordIds, viewId]);

  return useMemo(
    () => ({
      loadMore: () => {
        !disable && pageSize && loadPages && setLoadPages(pageSize + loadPages);
      },
      showMore: !!(!disable && pageSize && loadPages && loadPages < originRecordIds.length),
      recordIds,
    }),
    [disable, loadPages, originRecordIds.length, pageSize, recordIds]
  );
};

export const BitableLoadMoreContext = createModel(useBitableLoadMore);

export const useBitableLoadMoreContext = () => useModel(BitableLoadMoreContext);
